import React, { Component } from 'react'
import { AiOutlineSearch, AiFillTags } from "react-icons/ai"
import { getInformationWithDataV3 } from '../../services/CABE'
import { getValueCookie } from '../../services/cookieService'
import "../../css/general-style.css"
import '../../css/dashboard.css'
import { Menu } from "../../components/Menu/Menu"

const BTN_SECONDARY = { background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '8px', color: '#0F172A', fontWeight: 600, padding: '0.4rem 0.9rem', fontSize: '0.8rem', cursor: 'pointer' }

const LEVEL_STYLES = {
    1: { bg: 'rgba(37,99,235,0.08)', color: '#1e40af' },
    2: { bg: 'rgba(139,92,246,0.08)', color: '#5b21b6' },
    3: { bg: 'rgba(16,185,129,0.08)', color: '#065F46' },
    4: { bg: 'rgba(245,158,11,0.08)', color: '#92400E' },
}

/**
 * Customer Price Levels — new admin page (2026-08-19, tiered pricing project).
 *
 * Client: "hay clientes con precios especiales, clientes nivel 1, 2, 3."
 * (extended 2026-08-26 to Level 4). This page manages ONLY the assignment
 * (which tier a customer is in) — the actual price per tier is set in Price
 * Management (product_pricing.level1/2/3/4Price).
 * A customer with no row here defaults to Level 1, same default the pricing
 * endpoints use (see customerPriceLevel.ts / productPricing.ts getMap).
 *
 * Only TypeUser === '1' can change a level; everyone else sees it read-only —
 * same gate as Price Management. Every change updates customer_price_level
 * directly (no history table for this one — the level itself, not a price, so
 * lower audit stakes; product_pricing_history already covers the $ side).
 *
 * Route grouping (2026-08-20, client's ask — "agrupar por ruta, como en Price
 * Management"): route comes from MAS90's UDF_RUTA (AR_Customer), not from
 * anything in the local `customer` table — see customerPriceLevel.ts's
 * getList header for the mirror-table join. Same grouped-pagination shape as
 * ProductPricing.js: `routes` is the current page's groups (route name +
 * customer count), `customers` is every customer across those routes, never
 * split mid-route across pages. `pageSize` counts ROUTES, not customers.
 */
export default class CustomerPriceLevels extends Component {
    state = {
        idCompany: getValueCookie('CompanyId') || '',
        customers: [],
        // Route groups on the CURRENT page, in display order, each with
        // {route, customerCount} — route === '' means no UDF_RUTA assigned,
        // displayed as "(No route)". `customers` holds every customer across
        // ALL these routes (never truncated mid-route).
        routes: [],
        // Route filter's dropdown options — ALL routes for this company,
        // independent of search/pagination (fetched once on mount).
        routeOptions: [],
        routeFilter: undefined, // undefined = all routes; '' = "(No route)" specifically
        search: '',
        page: 1,
        pageSize: 10,
        total: 0,
        loading: false,
        loaded: false,
        errorMessage: '',
        savingCustomerNo: '',
    }

    isAdmin = getValueCookie('TypeUser') === '1'

    componentDidMount() {
        this.fetchCustomers()
        this.fetchRouteOptions()
    }

    async fetchCustomers() {
        this.setState({ loading: true, errorMessage: '' })
        try {
            const resp = await getInformationWithDataV3('/v3/pricing/customer-level/list', {
                idCompany: Number(this.state.idCompany),
                search: this.state.search || undefined,
                route: this.state.routeFilter,
                page: this.state.page,
                pageSize: this.state.pageSize,
            })
            if (!resp || !resp.status || resp.status.code !== 1) {
                this.setState({ loading: false, loaded: true, errorMessage: 'Could not load customers.' })
                return
            }
            const customers = (resp.data && Array.isArray(resp.data.customers)) ? resp.data.customers : []
            const routes = (resp.data && Array.isArray(resp.data.routes)) ? resp.data.routes : []
            this.setState({ loading: false, loaded: true, customers, routes, total: resp.total || routes.length })
        } catch (error) {
            this.setState({ loading: false, loaded: true, errorMessage: error.message || 'Error loading customers.' })
        }
    }

    /** Populates the route filter's options — independent of search/page. */
    async fetchRouteOptions() {
        try {
            const resp = await getInformationWithDataV3('/v3/pricing/customer-level/routes', { idCompany: Number(this.state.idCompany) })
            if (resp && resp.status && resp.status.code === 1 && Array.isArray(resp.data)) {
                this.setState({ routeOptions: resp.data })
            }
        } catch (error) {
            console.error('fetchRouteOptions failed:', error)
        }
    }

    async setLevel(customerNo, priceLevel) {
        this.setState({ savingCustomerNo: customerNo })
        try {
            const resp = await getInformationWithDataV3('/v3/pricing/customer-level/upsert', {
                customerNo,
                idCompany: Number(this.state.idCompany),
                priceLevel,
            })
            if (!resp || !resp.status || resp.status.code !== 1) {
                this.setState({ savingCustomerNo: '', errorMessage: 'Could not update ' + customerNo + '.' })
                return
            }
            this.setState((prev) => ({
                savingCustomerNo: '',
                customers: prev.customers.map((c) => c.customerNo === customerNo ? { ...c, priceLevel } : c),
            }))
        } catch (error) {
            this.setState({ savingCustomerNo: '', errorMessage: error.message || 'Error updating level.' })
        }
    }

    render() {
        const { customers, routes, routeOptions, routeFilter, search, page, pageSize, total, loading, errorMessage, savingCustomerNo } = this.state
        const totalPages = Math.max(1, Math.ceil(total / pageSize))

        const customersByRoute = {}
        for (const c of customers) {
            const key = c.route || ''
            if (!customersByRoute[key]) customersByRoute[key] = []
            customersByRoute[key].push(c)
        }
        // Route filter <select>'s value must stay a string ('' both for "All
        // routes" — represented as undefined in state — and for "(No route)")
        // while routeFilter itself keeps the undefined/'' distinction the
        // backend needs (see fetchCustomers). ALL_ROUTES is the sentinel that
        // tells onChange which one the admin actually picked.
        const ALL_ROUTES = '__all__'
        const routeSelectValue = routeFilter === undefined ? ALL_ROUTES : routeFilter

        return (
            <div className="dash-page" style={{ paddingTop: '30px' }}>
                <Menu />

                <div style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 60%, #2563EB 100%)', padding: '2rem 2rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <div style={{ background: 'rgba(255,255,255,0.12)', borderRadius: '12px', padding: '0.75rem', display: 'flex' }}>
                        <AiFillTags style={{ color: 'white', fontSize: '1.75rem' }} />
                    </div>
                    <div>
                        <h1 style={{ color: 'white', fontWeight: 800, fontSize: '1.5rem', margin: 0 }}>Customer Price Levels</h1>
                        <p style={{ color: 'rgba(255,255,255,0.65)', margin: '0.25rem 0 0', fontSize: '0.85rem' }}>
                            Assign each customer's price tier — unassigned customers use Level 1
                        </p>
                    </div>
                </div>

                <div style={{ padding: '1.5rem 2rem' }}>
                    <div className="card mb-3">
                        <div className="card-body" style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                            <div style={{ flex: 1, minWidth: '220px' }}>
                                <div style={{ position: 'relative' }}>
                                    <AiOutlineSearch style={{ position: 'absolute', left: '10px', top: '10px', color: '#94A3B8' }} />
                                    <input type="text" className="form-control" style={{ paddingLeft: '32px' }}
                                        placeholder="Search customer no. or name..." value={search}
                                        onChange={(e) => this.setState({ search: e.target.value })}
                                        onKeyDown={(e) => { if (e.key === 'Enter') this.setState({ page: 1 }, () => this.fetchCustomers()) }} />
                                </div>
                            </div>
                            {/* Route filter (2026-08-20) — audit one route at a time */}
                            <div style={{ minWidth: '220px' }}>
                                <select className="form-select" value={routeSelectValue}
                                    onChange={(e) => {
                                        const v = e.target.value
                                        this.setState({ routeFilter: v === ALL_ROUTES ? undefined : v, page: 1 }, () => this.fetchCustomers())
                                    }}>
                                    <option value={ALL_ROUTES}>All routes</option>
                                    {routeOptions.map((r) => (
                                        <option key={r.route || '(none)'} value={r.route || ''}>
                                            {(r.route || '(No route)') + ' (' + r.customerCount + ')'}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <button style={{ background: '#2563EB', border: 'none', borderRadius: '8px', color: '#FFFFFF', fontWeight: 700, padding: '0.5rem 1.1rem', fontSize: '0.85rem', cursor: 'pointer' }}
                                onClick={() => this.setState({ page: 1 }, () => this.fetchCustomers())}>Search</button>
                        </div>
                    </div>

                    {errorMessage && (
                        <div className="metric-card status-danger mb-3">
                            <div style={{ color: '#DC2626', fontSize: '0.85rem' }}>{errorMessage}</div>
                        </div>
                    )}

                    <div className="card">
                        <div className="tableFixHeadSmall">
                            <table className="table table-clean">
                                <thead>
                                    <tr>
                                        <th>Customer No.</th>
                                        <th>Customer Name</th>
                                        <th className="text-center">Price Level</th>
                                        <th>Last Updated By</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {routes.length === 0 && !loading && (
                                        <tr><td colSpan={4} className="empty">No results</td></tr>
                                    )}
                                    {routes.map((r) => (
                                        <React.Fragment key={r.route || '(none)'}>
                                            {/* Route group header (2026-08-20) — spans the full table, one
                                                per route, so the client can audit a whole route at a glance.
                                                customerCount is the group's own total, unaffected by
                                                pagination since a route is never split across pages. */}
                                            <tr style={{ background: '#F1F5F9' }}>
                                                <td colSpan={4} style={{ fontWeight: 800, fontSize: '0.8rem', color: '#334155', padding: '8px 12px' }}>
                                                    {r.route || '(No route)'}
                                                    <span style={{ marginLeft: '8px', fontWeight: 600, fontSize: '0.72rem', color: '#64748B' }}>
                                                        {r.customerCount} customer{r.customerCount === 1 ? '' : 's'}
                                                    </span>
                                                </td>
                                            </tr>
                                            {(customersByRoute[r.route || ''] || []).map((c) => {
                                                const isSaving = savingCustomerNo === c.customerNo
                                                return (
                                                    <tr key={c.customerNo}>
                                                        <td className="mono">{c.customerNo}</td>
                                                        <td className="small">{c.customerName}</td>
                                                        <td className="text-center">
                                                            <div style={{ display: 'inline-flex', gap: '6px' }}>
                                                                {[1, 2, 3, 4].map((lvl) => {
                                                                    const active = Number(c.priceLevel) === lvl
                                                                    const s = LEVEL_STYLES[lvl]
                                                                    return (
                                                                        <button key={lvl}
                                                                            disabled={!this.isAdmin || isSaving}
                                                                            onClick={() => this.setLevel(c.customerNo, lvl)}
                                                                            style={{
                                                                                minWidth: '34px', padding: '4px 10px', borderRadius: '999px',
                                                                                fontSize: '0.78rem', fontWeight: 700, cursor: this.isAdmin ? 'pointer' : 'default',
                                                                                background: active ? s.bg : 'transparent',
                                                                                color: active ? s.color : '#94A3B8',
                                                                                border: '1px solid ' + (active ? s.color + '55' : '#E2E8F0'),
                                                                                opacity: isSaving ? 0.6 : 1,
                                                                            }}>
                                                                            {lvl}
                                                                        </button>
                                                                    )
                                                                })}
                                                            </div>
                                                        </td>
                                                        <td className="small">{c.lastUpdatedBy || '—'}</td>
                                                    </tr>
                                                )
                                            })}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '0.75rem', padding: '12px 16px' }}>
                            <span style={{ fontSize: '0.8rem', color: '#64748B' }}>Page {page} of {totalPages} ({total} routes)</span>
                            <button style={{ ...BTN_SECONDARY, opacity: page <= 1 ? 0.5 : 1 }}
                                disabled={page <= 1} onClick={() => this.setState({ page: page - 1 }, () => this.fetchCustomers())}>Previous</button>
                            <button style={{ ...BTN_SECONDARY, opacity: page >= totalPages ? 0.5 : 1 }}
                                disabled={page >= totalPages} onClick={() => this.setState({ page: page + 1 }, () => this.fetchCustomers())}>Next</button>
                        </div>
                    </div>
                </div>
            </div>
        )
    }
}
